/**
 * Page Profile Mode Orchestrator
 *
 * Sequential orchestration for page profile-based page generation:
 * 1. Generate page outline from structured product data (Haiku)
 * 2. Generate global styles (Haiku)
 * 3. Generate sections one by one (Sonnet, non-streaming)
 */

import type { StylesConfigDto } from '@builder4/shared/dto/styles';
import type {
  PageProfileInput,
  PageProfileOutline,
  PageProfileCallbacks,
  PageProfileOptions,
  GeneratedSection,
} from './types/page-profile';
import { generatePageProfilePlan } from './stages/page-profile-plan';
import { generatePageProfileStyle, type PageProfileStyleResult } from './stages/page-profile-style';
import { generateSectionsFromOutline } from './stages/page-profile-generate';

// Re-export types for convenience
export type { PageProfileInput, PageProfileOutline, PageProfileCallbacks, PageProfileOptions };
export type { PageProfileStyleResult };

/**
 * Result of page profile orchestration
 */
export interface PageProfileResult {
  outline: PageProfileOutline;
  style: StylesConfigDto;
  styleLabel: string;
  sections: GeneratedSection[];
}

/**
 * Orchestrate page profile generation
 *
 * Flow:
 * 1. Generate page outline from structured product data
 * 2. Generate global styles based on product context
 * 3. Generate sections one by one with context passing
 *
 * @param options - PageProfileOptions containing input data and existing styles
 * @param callbacks - Callbacks for progress updates and results
 * @returns PageProfileResult with outline, style, and generated sections
 */
export const orchestratePageProfileGeneration = async (
  options: PageProfileOptions,
  callbacks: PageProfileCallbacks,
): Promise<PageProfileResult> => {
  const { input, existingStyles, signal, model, stats } = options;

  let outline: PageProfileOutline;
  let styleForGeneration: StylesConfigDto = existingStyles;
  let styleLabel = 'Existing Style';

  // Stage 1: PLAN
  callbacks.onProgress({
    phase: 'planning',
    message: 'Analyzing product data and planning page structure...',
  });

  try {
    outline = await generatePageProfilePlan(input, signal, undefined, stats);
    callbacks.onPlanComplete(outline);
    console.log('[PageProfile] Plan complete:', outline.sections.length, 'sections');
  } catch (error) {
    console.error('[PageProfile] Planning failed:', error);
    callbacks.onError(error instanceof Error ? error : new Error(String(error)));
    throw error;
  }

  // Stage 2: STYLE
  callbacks.onProgress({
    phase: 'styling',
    message: 'Generating visual style...',
  });

  try {
    const styleResult = await generatePageProfileStyle(input, signal, undefined, stats);
    styleForGeneration = styleResult.style;
    styleLabel = styleResult.label;
    callbacks.onStyleGenerated(styleResult.style, styleResult.label);
    console.log('[PageProfile] Style generated:', styleResult.label);
  } catch (error) {
    console.error('[PageProfile] Style generation failed:', error);
    // Style failure is non-fatal - continue with existing style
    callbacks.onError(new Error('Failed to generate style. Proceeding with existing style.'));
  }

  // Stage 3: GENERATE SECTIONS
  callbacks.onProgress({
    phase: 'generating-sections',
    message: `Generating section 1 of ${outline.sections.length}...`,
    sectionIndex: 0,
    totalSections: outline.sections.length,
  });

  const generatedSections: GeneratedSection[] = [];

  try {
    await generateSectionsFromOutline({
      outline,
      styles: styleForGeneration,
      model,
      stats,
      callbacks: {
        onSectionComplete: (section) => {
          generatedSections.push(section);
          callbacks.onSectionComplete(section);

          // Update progress for next section
          const nextIndex = section.index + 1;
          if (nextIndex < outline.sections.length) {
            callbacks.onProgress({
              phase: 'generating-sections',
              message: `Generating section ${nextIndex + 1} of ${outline.sections.length}...`,
              sectionIndex: nextIndex,
              totalSections: outline.sections.length,
            });
          }
        },
        onError: callbacks.onError,
      },
      signal,
    });
  } catch (error) {
    console.error('[PageProfile] Section generation failed:', error);
    callbacks.onError(error instanceof Error ? error : new Error(String(error)));
    throw error;
  }

  // Complete
  callbacks.onProgress({
    phase: 'complete',
    message: 'Page generation complete',
  });
  callbacks.onComplete();

  return {
    outline,
    style: styleForGeneration,
    styleLabel,
    sections: generatedSections,
  };
};
